import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Button from "@/components/base/Button";
import { exportFichePdf } from "@/utils/fichePdf";
import type { FicheConfig } from "@/types/fiche";
import type { Cycle, SchoolClass } from "@/types";
import FicheDocument from "./FicheDocument";

interface Props {
  open: boolean;
  onClose: () => void;
  config: FicheConfig;
  classes: SchoolClass[];
  cycles: Cycle[];
}

export default function FichePrintModal({ open, onClose, config, classes, cycles }: Props) {
  const [showDueDates, setShowDueDates] = useState(true);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="print-portal fixed inset-0 z-[110] overflow-y-auto bg-foreground-950/45 backdrop-blur-[2px]">
      <div className="no-print sticky top-0 z-10 border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3 px-4 py-3 md:px-8">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-50 text-primary-700">
              <i className="ri-file-text-line text-lg"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold font-heading text-foreground-950">
                Fiche de renseignements
              </h3>
              <p className="text-xs text-foreground-500">
                Année scolaire {config.header.schoolYear} — aperçu prêt à imprimer
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowDueDates((v) => !v)}
              aria-pressed={showDueDates}
              className={`inline-flex items-center gap-2 whitespace-nowrap rounded-md border px-3 py-2 text-xs font-medium transition-colors cursor-pointer ${
                showDueDates
                  ? "border-primary-300 bg-primary-50 text-primary-800"
                  : "border-background-300 bg-background-50 text-foreground-600 hover:border-background-400"
              }`}
            >
              <i className={showDueDates ? "ri-eye-line" : "ri-eye-off-line"}></i>
              Dates d'échéance
            </button>
            <Button
              variant="outline"
              icon="ri-file-pdf-2-line"
              onClick={() => exportFichePdf(config, classes, cycles, showDueDates)}
            >
              Exporter en PDF
            </Button>
            <Button icon="ri-printer-line" onClick={() => window.print()}>
              Imprimer
            </Button>
            <button
              onClick={onClose}
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-800 cursor-pointer"
              aria-label="Fermer"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="print-pad px-4 py-6 md:px-8 md:py-10">
        <div className="mx-auto w-full max-w-[820px]">
          <FicheDocument config={config} classes={classes} cycles={cycles} showDueDates={showDueDates} />
        </div>
      </div>
    </div>,
    document.body,
  );
}